import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import type { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { of } from 'rxjs';
import { UserRegistrationPort } from '../../../domain/commerce-registry/ports/user-registration.port';
import type {
  RegisterUserResult,
  RegisterUserV2Payload,
} from '../../../domain/commerce-registry/models/register-user.model';
import { ENVIRONMENT } from '../../../../environments/environment';

/** Same as legacy `CodigoRespuesta.OK` / registration-validation adapter. */
const CODE_OK = 100;

interface RegistrarV2ApiResponse {
  code?: number;
  message?: string;
  data?: unknown;
}

@Injectable()
export class UserRegistrationApiAdapter extends UserRegistrationPort {
  constructor(private readonly http: HttpClient) {
    super();
  }

  register(payload: RegisterUserV2Payload): Observable<RegisterUserResult> {
    const base = ENVIRONMENT.apiMobileUrl?.trim();
    if (!base) {
      return of<RegisterUserResult>({
        outcome: 'error',
        message: 'Servicio de registro no configurado',
      });
    }

    const url = `${base.replace(/\/$/, '')}/api/usuarios/registrarv2`;
    return this.http.post<RegistrarV2ApiResponse>(url, payload).pipe(
      map((res) => this.mapResponse(res)),
      catchError(() =>
        of<RegisterUserResult>({
          outcome: 'error',
          message: 'No pudimos completar el registro. Intenta de nuevo.',
        }),
      ),
    );
  }

  private mapResponse(res: RegistrarV2ApiResponse): RegisterUserResult {
    const code = res?.code;
    const message = typeof res?.message === 'string' ? res.message.trim() : '';
    if (code === CODE_OK) {
      const uid = extractUidFromRegistrarData(res?.data);
      return {
        outcome: 'success',
        message: message || 'Tu registro se completó correctamente.',
        ...(uid ? { uid } : {}),
      };
    }
    return {
      outcome: 'error',
      message: message || 'No se pudo completar el registro.',
    };
  }
}

/** Reads SAAS uid from registrarv2 `data` (object with `uid`/`UID`, or nested). */
export function extractUidFromRegistrarData(data: unknown): string | undefined {
  if (data == null) return undefined;
  if (typeof data === 'string') {
    const t = data.trim();
    return t || undefined;
  }
  if (typeof data !== 'object') return undefined;
  const o = data as Record<string, unknown>;
  const raw = o['uid'] ?? o['UID'] ?? o['Uid'];
  if (typeof raw === 'string' && raw.trim()) return raw.trim();
  return undefined;
}
